import { Status } from 'services/api/api-client.types.ts';
import { HTMLAttributes, ReactNode, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import styles from './BulkUpdateStatusModal.module.scss';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';
import clsx from 'clsx';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Field } from 'components/uikit/Field.tsx';
import { HookFormDropDownInput } from 'components/uikit/inputs/dropdown/HookFormDropDownInput.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { QueryFactory } from 'services/api';
import { useQueryClient } from '@tanstack/react-query';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { FileNodeState } from '../../SelectionStore.ts';
import {
  getDocumentStatusStyle,
  useGetLocalizedStatus,
} from '../../../statuses/statusItem/documentStatusHelpers.ts';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useSelectModeContext } from '../../useSelectModeContext.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';

type BulkUpdateStatusModalProps = {
  selectedDocuments: FileNodeState[];
  isOpen: boolean;
  onClose: () => void;
};

export const BulkUpdateStatusModal = (props: BulkUpdateStatusModalProps) => {
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('Component.BulkUpdateStatusModal');

  const [isLoading, setIsLoading] = useState(false);

  const { setIsSelectMode } = useSelectModeContext();
  const getLocalizedStatus = useGetLocalizedStatus();

  const form = useAdvancedForm<{ status: Status }>(
    useCallback(
      async (data) => {
        setIsLoading(true);

        try {
          const patchEntries = props.selectedDocuments.map((d) => ({
            id: d.documentId,
            patchDto: { status: data.status },
          }));
          await QueryFactory.DocumentClient.deferBulkPatch(patchEntries);
        } catch (e) {
          showErrorToast(e);
        }

        await onModalClose();

        setIsLoading(false);
        setIsSelectMode(false);
      },
      [props.selectedDocuments],
    ),
    { defaultValues: { status: Status.Draft } },
  );

  const onModalClose = async () => {
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
    });

    form.reset();
    form.clearErrors();
    props.onClose();
  };

  const renderSelectStatusOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: Status,
  ): ReactNode => {
    if (!option) return <></>;
    return (
      <Box {...props} component="li" key={`option-${option}`}>
        <div className={getDocumentStatusStyle(option)}>
          {getLocalizedStatus(option)}
        </div>
      </Box>
    );
  };

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onModalClose}
      isBlocking
      title={i18n.t('title')}
    >
      <Loading loading={isLoading}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field title={i18n.t('status_field')}>
            <HookFormDropDownInput
              name={'status'}
              control={form.control}
              options={[
                Status.Draft,
                Status.ReadyForReview,
                Status.CanBeSigned,
                Status.NotApplicable,
                Status.Signed,
              ]}
              renderOption={renderSelectStatusOption}
              renderInput={(params) => (
                <Input
                  {...params}
                  value={getLocalizedStatus(params.selectedValue)}
                />
              )}
              className={styles.selector}
              classes={dropdownClasses}
            />
          </Field>
          <div className={styles.footer}>
            <Button
              title={i18n.t('cancel_button')}
              className={styles.button}
              color={ButtonColor.Secondary}
              onClick={onModalClose}
            />
            <Button
              title={i18n.t('ok_button')}
              className={styles.button}
              type={'submit'}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};

const dropdownClasses = {
  option: clsx(dropdownStyles.selectorOption, styles.statusOption),
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
};
